// Ghost — board ToileWallRest (`.t.ghost`), verbatim values at the cell sizes the board drew
// (generous). Below that, the cell is whatever `justify.ts` actually produces — as narrow as a
// packed phone row's ~77–120px, occasionally narrower still — and fixed 15px type there wrapped
// the rule to 4–5 lines and ran the own-act line out of the cell entirely (operator-observed bug,
// 2026-09-19). `container-type: inline-size` (the same primitive `WorkingCell.module.scss`'s numeral
// already uses) makes every size below a function of THIS box's own width via `cqw`. A SINGLE
// container axis is enough for both width and height: the ghost's box is always exactly 2:3 (its
// own `aspectRatio` style, and `justify.ts`'s own `GHOST_CELL = {width: 2, height: 3}` — the cell
// this file is skinning is, by construction, `height = 1.5 × width`, always), so a width-only query
// already knows the height too; `container-type: size` would ask CSS to establish a second axis
// this box never varies independently.
//
// Revision 2026-09-19 (operator correction — reverses the first pass's "shrink to fit, pen dropped
// first"): 8–8.5px is not legible. The floor is now 11px for both sentences, never crossed. Below
// the width where both no longer fit AT 11px, the OWN sentence — never the type size — yields: the
// split (two-sentence) cell shows the rule and the pen alone, the pen now standing for the dropped
// act. Only where even the rule and the pen no longer fit at 11px does the pen go too, the rule
// standing alone. A sentence is never clipped, never ellipsed, never sized below 11px — see the
// `@container` ladder at the bottom of this file for the three thresholds and the arithmetic behind
// each. `.rule`/`.words`/`.pen`'s own rules must stay BEFORE that ladder — `@container` carries no
// specificity of its own, so a matching rule there wins only by coming later in the cascade.
.ghost {
    // A real <button> (Ghost.tsx) — one control, one name — so it resets the UA button box first.
    appearance: none;
    font: inherit;
    width: 100%;
    margin: 0;
    border-radius: var(--tl-radius-art-xs);
    background: color-mix(in srgb, var(--tl-accent) 4%, transparent);
    border: 1px dashed color-mix(in srgb, var(--tl-accent) 45%, transparent);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: clamp(4px, 5cqw, 12px);
    padding: clamp(8px, 9cqw, 18px);
    text-align: center;
    cursor: pointer;
    min-width: 0;
    container-type: inline-size;

    &:focus-visible {
        outline: 2px solid var(--tl-accent);
        outline-offset: 2px;
    }
}

// The rest state's two-sentence pair (board `FirstGlance`, validated 2026-09-18): the rule pinned
// toward the top, the own-act row toward the bottom, the cell's own height between them. Applied
// only when a house carries a rule — a ruleless ghost (Ovid) keeps `.ghost`'s plain centring above,
// unchanged.
.split {
    justify-content: space-between;
}

// The wall's one stated rule — the primary text colour, never the accent: the accent marks this
// CELL's own act below, not the rule, or the tap would seem to promise the rule itself. Sized off
// the cell's own inline size (`cqw`), floored at 11px (operator, 2026-09-19 — the priority when
// space is short is no longer a smaller floor for the own sentence; it is `.ownRow .words` yielding
// outright, in the `@container` ladder below).
.rule {
    font-family: var(--tl-font);
    font-size: clamp(11px, 8cqw, 15px);
    line-height: 1.25;
    color: var(--tl-text);
    max-width: 82%;
    overflow-wrap: break-word;
}

.ownRow {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
}

// The verb made visible under a query (board `FirstGlance` frame 4) — the same sentence the
// accessible name already carries (`Ghost.tsx`'s `GHOST_VERB_LABEL`), read once.
.verb {
    font-family: var(--tl-font-label);
    font-size: 9.5px;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--tl-muted);
}

.pen {
    width: 22px;
    height: 22px;
    color: var(--tl-accent);
    display: block;
    opacity: 0.9;

    svg {
        width: 100%;
        height: 100%;
        display: block;
    }
}

// The own-act's words (the two-sentence rest state's bottom line, Ovid's single sentence, and the
// query state's typed words alike — every branch that reaches `.words` shares this sizing and this
// 11px floor). Under a query specifically, a very long typed search still only wraps — no line
// clamp exists here today, and this revision does not add one (operator, 2026-09-19: "tell me what
// it does today rather than changing it").
.words {
    font-family: var(--tl-font);
    font-size: clamp(11px, 8cqw, 15px);
    line-height: 1.25;
    color: var(--tl-accent);
    max-width: 82%;
    overflow-wrap: break-word;
}

// ── The narrow-cell ladder (operator correction, 2026-09-19) ──────────────────────────────────────
// Three independent `@container` blocks, compounding as the cell narrows — a cell matching all
// three (very narrow) gets all three overrides at once; one matching only the widest gets only that
// one. Thresholds are read off a deterministic fit model (line-wrap by character count at the
// clamp's own floor font-size, against the cell's own height, `height = 1.5 × width`) run across
// the justifier's own realistic bands (this file's header doc; the model itself is not committed —
// a throwaway script against `justify.ts`), with a conservative 0.58×font-size average character
// width (wider than a real Archivo glyph averages, which UNDERstates how many characters fit per
// line and so OVERstates the lines needed — the safe direction for a fit check) and a fixed 8px
// gap between the pen and the pen-only `.ownRow` where that applies.
//
// 140px — free the reading-room margin. `.rule`/`.words` drop their 82% max-width to the box's
// full content width: strictly a fitting AID (more width per line, fewer lines), not a priority
// step, so it engages well above where anything actually needs to hide.
//
// 100px — the own sentence yields. The model: `.rule` (32 chars) + `.ownRow` (pen 22px + 8px gap +
// `.words`, 23 chars) no longer both fit at the 11px floor below ~87px measured width (rule+own
// together need ~119px of height there; the cell's own height at 87px width is 130.5px, so the
// true crossover sits close under 90px) — 100px keeps a working margin against the model's own
// approximation. `.ownRow .words` (scoped to the split rest state only, never Ovid's single
// sentence or the query state's typed words, which have no `.ownRow` to nest in) goes to
// `display: none`; the pen alone now stands for the dropped act, in tungsten, unchanged.
//
// 70px — the pen goes too, the rule stands alone. The model: rule + pen alone stops fitting at
// 11px only below ~65-68px measured width (`.ownRow` there needs just 22px for the pen, so the
// binding constraint is `.rule`'s own block height against the cell's own shrinking height) — 70px
// keeps the same kind of margin. Comfortably below the justifier's own realistic phone floor
// (~77px, this file's header doc) — in practice this tier is a structural guarantee ("never
// clipped, ever") rather than a state any real device reaches; `.rule` alone keeps fitting at 11px
// down to roughly 60-65px in the same model, so the guarantee holds with room to spare even there.
@container (max-width: 140px) {
    .rule,
    .words {
        max-width: 100%;
    }
}

@container (max-width: 100px) {
    .ownRow .words {
        display: none;
    }
}

@container (max-width: 70px) {
    .pen {
        display: none;
    }
}
